import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2 } from 'lucide-react';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Shop = () => {
  useDocumentMeta({
    title: 'Shop | Revs Vibes',
    description: 'Browse the Revs Vibes shop for curated automotive merchandise and collector car culture goods.',
    canonical: 'https://www.revsvibes.com/shop',
  });
  const { language } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/shop`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setProducts(data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const t = {
    en: { title: 'SHOP', subtitle: 'Premium automotive merchandise and curated collections', addToCart: 'Add to Cart' },
    es: { title: 'TIENDA', subtitle: 'Mercancía automotriz premium y colecciones curadas', addToCart: 'Agregar al Carrito' },
    pt: { title: 'LOJA', subtitle: 'Mercadoria automotiva premium e coleções curadas', addToCart: 'Adicionar ao Carrinho' },
    fr: { title: 'BOUTIQUE', subtitle: 'Marchandise automobile premium et collections sélectionnées', addToCart: 'Ajouter au Panier' },
    it: { title: 'NEGOZIO', subtitle: 'Merchandising automobilistico premium e collezioni curate', addToCart: 'Aggiungi al Carrello' },
  };
  const tr = t[language] || t.en;

  return (
    <div className="min-h-screen pt-20">
      <section className="relative h-96 bg-black text-white overflow-hidden clickable-card">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1600" alt="Shop" className="w-full h-full object-cover opacity-40" />
        </div>
        <div className="relative h-full flex items-center justify-center">
          <div className="text-center px-4 max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-4" data-testid="shop-title">{tr.title}</h1>
            <p className="text-xl text-gray-300">{tr.subtitle}</p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gray-400" size={32} /></div>
          ) : products.length === 0 ? (
            <p className="text-center text-gray-400 py-20">No products available.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.map(product => (
                <Card key={product.id} className="clickable-card group overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-300" data-testid={`product-card-${product.id}`}>
                  <div className="relative h-72 overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    {product.category && (
                      <div className="absolute top-4 left-4 bg-black/70 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs">{product.category}</div>
                    )}
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold mb-1 text-black">{product.name}</h3>
                    {product.description && <p className="text-gray-600 text-sm mb-3 line-clamp-2">{product.description}</p>}
                    <div className="flex items-center justify-between mt-4">
                      <span className="text-xl font-bold text-black">{product.price}</span>
                      <Button className="bg-black hover:bg-gray-800 text-white" data-testid={`add-to-cart-${product.id}`}>{tr.addToCart}</Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Shop;
